import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { customPayRunGateway } from '../index';
import { listAllEmployees, peopleGateway } from '../../people';
import { payConfigGateway } from '../../pay-config';
import { payFrequencyLabels, type PayScheduleResponse } from '../../pay-config/types';
import type { EmployeeResponse } from '../../people/types';
import { CustomPayRunType, runTypeLabel, type CustomPayRunResponse, type DuplicatePreviewResponse } from '../types';
import { findResumableRun } from './findResumableRun';
import { DuplicateFlagsNotice } from './DuplicateFlagsNotice';
import { daysBefore, isoToday, payPeriodContaining, suggestedCheckDate } from './schedulePeriods';
import { formatUsDate, formatUsPeriod } from './runContext';
import { runStatusLabel } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { CPR_PERMISSIONS, NO_PERMISSION_NOTE } from '../permissions';
import { usePermissionCheck } from '../../identity/state/usePermissions';
import '../custom-pay-run.css';

const DEBOUNCE_MS = 300;

/**
 * Run Payroll → Custom Pay Run lands here (EPN-241). The preparer chooses the run type (Regular, or Bonus: additional earnings only), the employees, the pay schedule
 * (company schedules; preselected from the chosen employees' profile pay schedule when they all share one), the pay period and the pay day (suggested from the
 * schedule, always editable). A past pay day is allowed (a correction run) with a visible warning, never a block. While the choices change, the same-type duplicate
 * warning (run level and employee level) refreshes live. A newest open draft can still be resumed. Nothing here is a payroll decision: the server validates the
 * dates and every later step, and nothing is paid.
 */
export function CustomPayRunLandingPage() {
  const navigate = useNavigate();
  const can = usePermissionCheck();
  const canWrite = can(CPR_PERMISSIONS.write);
  const today = useMemo(() => isoToday(), []);

  // EPN-242: the pay run history's "New Regular Run" / "New Bonus Run" open this page with the type chosen (?type=regular|bonus). Anything else keeps the Regular default.
  const [searchParams] = useSearchParams();
  const [requestedType] = useState<number>(searchParams.get('type')?.toLowerCase() === 'bonus' ? CustomPayRunType.Bonus : CustomPayRunType.Regular);
  const [runType, setRunType] = useState<number>(requestedType);
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [schedules, setSchedules] = useState<PayScheduleResponse[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [scheduleId, setScheduleId] = useState('');
  const [periodStart, setPeriodStart] = useState(daysBefore(today, 13));
  const [periodEnd, setPeriodEnd] = useState(today);
  const [checkDate, setCheckDate] = useState(today);
  const [scheduleHint, setScheduleHint] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [resumable, setResumable] = useState<CustomPayRunResponse | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicatePreviewResponse>({ runWarnings: [], flags: [] });
  const [duplicatesUnavailable, setDuplicatesUnavailable] = useState(false);

  // What the person has changed by hand is never overwritten by a suggestion.
  const scheduleTouched = useRef(false);
  const datesTouched = useRef(false);
  const selectionTouched = useRef(false);
  const employeeSchedule = useRef(new Map<string, string | null>());

  // ---- load ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [all, sched, runs] = await Promise.all([
          listAllEmployees(peopleGateway, { sortBy: 'name' }),
          payConfigGateway.listPaySchedules().catch(() => [] as PayScheduleResponse[]),
          customPayRunGateway.list().catch(() => [] as CustomPayRunResponse[]),
        ]);
        if (cancelled) return;
        const active = all.filter((e) => e.status === 0);
        setEmployees(active);
        setSchedules(sched.filter((s) => s.isActive));
        setResumable(findResumableRun(runs, 'any'));
        setSelected(requestedType === CustomPayRunType.Bonus ? new Set() : new Set(active.map((e) => e.id))); // Regular ticks every active employee; a Bonus run (chosen from the history page) starts empty
      } catch (err) {
        if (!cancelled) setLoadError(friendlyErrorMessage(err, 'Could not load the employees for this run. You can still start it and choose them in the next step.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestedType]);

  function chooseRunType(next: number) {
    setRunType(next);
    // Regular starts with every active employee ticked; a Bonus run starts empty (nobody ticked) until the preparer chooses. Only until they touch the list.
    if (!selectionTouched.current) setSelected(next === CustomPayRunType.Bonus ? new Set() : new Set(employees.map((e) => e.id)));
  }

  function toggle(id: string) {
    selectionTouched.current = true;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ---- schedule preselection from the chosen employees' profile pay schedule ----
  const selectedKey = useMemo(() => [...selected].sort().join(','), [selected]);
  useEffect(() => {
    if (loading || selectedKey === '') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setScheduleHint(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const ids = selectedKey.split(',');
      await Promise.all(
        ids
          .filter((id) => !employeeSchedule.current.has(id))
          .map(async (id) => {
            try {
              employeeSchedule.current.set(id, (await peopleGateway.getEmployeePaySchedule(id)).current?.payScheduleId ?? null);
            } catch {
              employeeSchedule.current.set(id, null);
            }
          }),
      );
      if (cancelled) return;
      const assigned = ids.map((id) => employeeSchedule.current.get(id) ?? null);
      const distinct = [...new Set(assigned.filter((v): v is string => v !== null))];
      const everyoneHasOne = assigned.every((v) => v !== null);
      if (distinct.length === 1 && everyoneHasOne) {
        setScheduleHint('Preselected from the pay schedule on the chosen employees’ profiles. You can change it.');
        if (!scheduleTouched.current) setScheduleId(distinct[0]!);
      } else if (distinct.length > 1) {
        setScheduleHint('The chosen employees are on different pay schedules. Choose the one this run should use.');
        if (!scheduleTouched.current) setScheduleId('');
      } else if (distinct.length === 1) {
        setScheduleHint('Some of the chosen employees have no pay schedule on their profile. Choose the one this run should use.');
      } else {
        setScheduleHint('The chosen employees have no pay schedule on their profile. Choose one, or start without one.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedKey, loading]);

  // ---- dates suggested from the schedule ----
  const schedule = schedules.find((s) => s.id === scheduleId) ?? null;
  useEffect(() => {
    if (!schedule || datesTouched.current) return;
    const period = payPeriodContaining(schedule, today);
    if (!period) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPeriodStart(period.start);
    setPeriodEnd(period.end);
    setCheckDate(suggestedCheckDate(period, today));
  }, [schedule, today]);

  // ---- validation ----
  const periodProblem = !periodStart || !periodEnd ? 'Choose the pay period start and end.' : periodEnd <= periodStart ? 'The pay period must end after it starts.' : null;
  const checkProblem = !checkDate ? 'Choose the pay day.' : null;
  const checkInPast = !!checkDate && checkDate < today;
  const valid = !periodProblem && !checkProblem;

  // ---- live duplicate warnings ----
  useEffect(() => {
    if (loading || !valid) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      customPayRunGateway
        .previewDuplicates({ runType, periodStartDate: periodStart, periodEndDate: periodEnd, checkDate, employeeIds: selectedKey ? selectedKey.split(',') : [] }, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return;
          setDuplicates(result);
          setDuplicatesUnavailable(false);
        })
        .catch(() => {
          // Context only: the server enforces the acknowledgement when the run is sent for approval either way.
          if (!controller.signal.aborted) setDuplicatesUnavailable(true);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [loading, valid, runType, periodStart, periodEnd, checkDate, selectedKey]);

  const start = useCallback(async () => {
    if (!valid || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await customPayRunGateway.create({
        runType,
        payScheduleId: scheduleId || null,
        periodStartDate: periodStart,
        periodEndDate: periodEnd,
        checkDate,
        description: null,
        stateCode: null,
        idempotencyKey: crypto.randomUUID(),
      });
      // The chosen employees are saved with the run, so Step 1 opens with exactly them (a run with saved choices is never defaulted again).
      if (selected.size > 0) {
        await customPayRunGateway.setEmployees(created.id, employees.map((e) => ({ employeeId: e.id, isIncluded: selected.has(e.id), acknowledgeStatusWarning: false })));
      }
      navigate(`/payroll-processing/${created.id}/custom-payroll`);
    } catch (err) {
      setCreateError(friendlyErrorMessage(err, 'Could not start a custom pay run.'));
      setCreating(false);
    }
  }, [valid, creating, runType, scheduleId, periodStart, periodEnd, checkDate, selected, employees, navigate]);

  const visibleWarnings = valid && !duplicatesUnavailable ? duplicates : { runWarnings: [], flags: [] };

  return (
    <section className="card card--wide cpr cpr-landing">
      <div className="section-header">
        <h2>Custom Pay Run</h2>
        <button type="button" className="link" onClick={() => navigate('/payroll-processing/history')}>
          Pay run history
        </button>
      </div>
      <p className="muted">
        Start an off-cycle payroll. Choose what kind of run it is, who is in it and which pay period and pay day it covers. Nothing is paid by starting it: you review the
        calculation, then send it for approval.
      </p>

      {resumable && (
        <div className="cpr-notice" role="region" aria-label="Resume your draft" data-testid="resume-draft">
          <strong>You have an unsent draft:</strong> {runTypeLabel(resumable.runType)}, {formatUsPeriod(resumable)}, pay day {formatUsDate(resumable.checkDate)} (
          {runStatusLabel(resumable.status)}).{' '}
          <button type="button" className="cpr-btn cpr-btn--outline" onClick={() => navigate(`/payroll-processing/${resumable.id}/custom-payroll`)}>
            Resume draft
          </button>
        </div>
      )}

      {loadError && (
        <p className="cpr-notice cpr-notice--error" role="alert">
          {loadError}
        </p>
      )}

      <form
        className="cpr-landing__form"
        onSubmit={(e) => {
          e.preventDefault();
          void start();
        }}
        noValidate
      >
        <fieldset className="cpr-landing__types">
          <legend>Run type</legend>
          <label>
            <input type="radio" name="runType" checked={runType === CustomPayRunType.Regular} onChange={() => chooseRunType(CustomPayRunType.Regular)} />
            <span>
              <strong>Regular run</strong>
              <span className="muted"> The employees&apos; regular pay for the period, plus any earnings you add.</span>
            </span>
          </label>
          <label>
            <input type="radio" name="runType" checked={runType === CustomPayRunType.Bonus} onChange={() => chooseRunType(CustomPayRunType.Bonus)} />
            <span>
              <strong>Bonus run (additional earnings)</strong>
              <span className="muted"> Starts empty: regular salary or hourly pay is not included. Only the earnings you add are paid.</span>
            </span>
          </label>
        </fieldset>

        <div className="cpr-fields">
          <label className="cpr-field">
            Pay schedule
            <select
              value={scheduleId}
              onChange={(e) => {
                scheduleTouched.current = true;
                setScheduleId(e.target.value);
              }}
            >
              <option value="">No pay schedule</option>
              {schedules.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({payFrequencyLabels[s.frequency] ?? '—'})
                </option>
              ))}
            </select>
          </label>
          <label className="cpr-field">
            Pay period starts
            <input
              type="date"
              value={periodStart}
              onChange={(e) => {
                datesTouched.current = true;
                setPeriodStart(e.target.value);
              }}
            />
          </label>
          <label className="cpr-field">
            Pay period ends
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => {
                datesTouched.current = true;
                setPeriodEnd(e.target.value);
              }}
            />
          </label>
          <label className="cpr-field">
            Pay day (check date)
            <input
              type="date"
              value={checkDate}
              onChange={(e) => {
                datesTouched.current = true;
                setCheckDate(e.target.value);
              }}
            />
          </label>
        </div>
        {scheduleHint && (
          <p className="muted" role="status" data-testid="schedule-hint">
            {scheduleHint}
          </p>
        )}
        {schedule && !payPeriodContaining(schedule, today) && (
          <p className="muted">This pay schedule has no start date to count from, so enter the pay period yourself.</p>
        )}
        {(periodProblem || checkProblem) && (
          <p className="cpr-notice cpr-notice--error" role="alert">
            {periodProblem ?? checkProblem}
          </p>
        )}
        {checkInPast && (
          <p className="cpr-notice cpr-notice--warning" role="status" data-testid="past-check-date">
            <strong>This pay day is in the past ({formatUsDate(checkDate)}).</strong> That is fine for a correction run, but check it is what you mean. Nothing stops you.
          </p>
        )}

        {visibleWarnings.runWarnings.length > 0 && (
          <div className="cpr-notice cpr-notice--warning" role="status" data-testid="landing-run-warnings">
            <strong>Possible duplicate run</strong>
            <ul>
              {visibleWarnings.runWarnings.map((w) => (
                <li key={w}>{w.replace(/status (\w+)\)/, (_m, status: string) => `${runStatusLabel(status).toLowerCase()})`)}</li>
              ))}
            </ul>
          </div>
        )}
        <DuplicateFlagsNotice flags={visibleWarnings.flags} />

        <fieldset className="cpr-landing__employees">
          <legend>
            Employees ({selected.size} of {employees.length} chosen)
          </legend>
          {loading && <p className="muted">Loading employees…</p>}
          {!loading && employees.length === 0 && <p className="muted">No active employees found. You can add employees in the next step.</p>}
          {runType === CustomPayRunType.Bonus && selected.size === 0 && employees.length > 0 && (
            <p className="muted">A bonus run starts empty. Tick the employees who should receive a bonus; you add each earning in the next step.</p>
          )}
          <ul className="cpr-list__items">
            {employees.map((e) => (
              <li key={e.id} className="cpr-list__item">
                <label>
                  <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} /> {`${e.firstName} ${e.lastName}`.trim()}
                </label>
              </li>
            ))}
          </ul>
        </fieldset>

        {createError && (
          <p className="cpr-notice cpr-notice--error" role="alert">
            {createError}
          </p>
        )}
        <div className="cpr-footer">
          <span className="muted">{!canWrite ? NO_PERMISSION_NOTE : ' '}</span>
          <button type="submit" className="cpr-btn cpr-btn--green" disabled={!valid || creating || !canWrite}>
            {creating ? 'Starting…' : `Start ${runTypeLabel(runType).toLowerCase()}`}
          </button>
        </div>
      </form>
    </section>
  );
}
